import { Box } from '@mui/material';
import Decimal from 'decimal.js';
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';

import { getCSSPropsForButtonPadding } from '@/components/form/baseInputs/Button/styles';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareInputRepeater } from '@/components/form/formAwareInputs/FormAwareInputRepeater/FormAwareInputRepeater';
import { InputRepeaterSumRow } from '@/components/form/formAwareInputs/FormAwareInputRepeater/InputRepeaterSumRow';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { determineDefaultAssetClassId } from '@/modules/assets/utils';
import { useAssetCategoryOptions } from '@/modules/entities/StructuredAssetsSubform/hooks/useAssetCategoryOptions';
import {
  AssetCategoryField,
  AssetStateField,
  AssetTitleField,
  AssetValueField,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.fields';
import {
  defaultValues as structuredAssetsDefaultValues,
  StructuredAsset,
} from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.types';
import { getOwnerAwareDefaultTaxSitus } from '@/modules/entities/StructuredAssetsSubform/StructuredAssetsSubform.utils';
import { getEntityPrincipalClientIds } from '@/modules/entities/utils/getEntityPrincipalClientIds';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { useUnguardedHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useDefaultAssetClassId } from '@/modules/tenant/TenantDetailsContext/hooks/useDefaultAssetClassId';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstatePage_EntityFragment } from '../../graphql/EstatePageData.generated';
import { AssetHoldingsFormShape } from './AddHoldingsFormModal.types';

export interface AssetHoldingFormProps {
  holdingsGroup?: EstatePage_EntityFragment;
  onSubmit: (data: AssetHoldingsFormShape) => void | Promise<void>;
  disabled?: boolean;
}

export interface AssetHoldingFormRef {
  submit: () => void;
  reset: () => void;
}

export const AssetHoldingForm = forwardRef<
  AssetHoldingFormRef,
  AssetHoldingFormProps
>(({ holdingsGroup, onSubmit, disabled = false }, ref) => {
  const { assetClassesById } = useTenantDetailsContext();
  const defaultAssetClassId = useDefaultAssetClassId();
  const { primaryClients } = useUnguardedHouseholdDetailsContext();
  // Single-grantor entity → owner's residency; joint/multi/unknown → primary grantor.
  const ownerPrincipalIds = useMemo(
    () => getEntityPrincipalClientIds(holdingsGroup),
    [holdingsGroup]
  );
  const defaultTaxSitus = useMemo(() => {
    return getOwnerAwareDefaultTaxSitus({
      primaryClients,
      ownerPrincipalIds,
    });
  }, [primaryClients, ownerPrincipalIds]);

  const entityType = holdingsGroup
    ? getEntityTypeFromEntityKind(holdingsGroup.kind)
    : null;
  const defaultCategoryId = useMemo(() => {
    if (!entityType) return defaultAssetClassId;
    return determineDefaultAssetClassId(
      entityType,
      defaultAssetClassId,
      assetClassesById
    );
  }, [defaultAssetClassId, entityType, assetClassesById]);

  const emptyAsset = useMemo<StructuredAsset>(
    () => ({
      categoryId: defaultCategoryId,
      title: '',
      value: new Decimal(0),
      integrationType: null,
      taxSitus: defaultTaxSitus,
      _originalTaxSitus: defaultTaxSitus,
    }),
    [defaultCategoryId, defaultTaxSitus]
  );

  const defaultStructuredAssetsData = useMemo(() => {
    return {
      ...structuredAssetsDefaultValues,
      assets: emptyAsset.categoryId ? [emptyAsset] : [],
    };
  }, [emptyAsset]);

  const formMethods = useForm<AssetHoldingsFormShape>(
    {
      defaultValues: {
        holdingsGroupId: holdingsGroup?.id || null,
        holdingType: 'asset',
        structuredAssetsSubform: defaultStructuredAssetsData,
        valuationAsOfDate: new Date(),
      },
    },
    'add-holdings-assets'
  );

  const { getFieldState, getValues, setValue } = formMethods;

  // Update owner defaults without replacing rows or fields the user has touched.
  useEffect(() => {
    setValue('holdingsGroupId', holdingsGroup?.id ?? null);
    const assets = getValues('structuredAssetsSubform.assets') ?? [];
    assets.forEach((_asset, idx) => {
      // Read current touched state without making field touches a synchronization trigger.
      const categoryField =
        `structuredAssetsSubform.assets.${idx}.categoryId` as const;
      const taxSitusField =
        `structuredAssetsSubform.assets.${idx}.taxSitus` as const;
      if (defaultCategoryId && !getFieldState(categoryField).isTouched) {
        setValue(categoryField, defaultCategoryId, { shouldDirty: false });
      }
      if (!getFieldState(taxSitusField).isTouched) {
        setValue(taxSitusField, defaultTaxSitus, { shouldDirty: false });
        // Keep the analytics baseline aligned with automatic state defaults.
        setValue(
          `structuredAssetsSubform.assets.${idx}._originalTaxSitus` as const,
          defaultTaxSitus,
          { shouldDirty: false }
        );
      }
    });
  }, [
    holdingsGroup?.id,
    defaultCategoryId,
    defaultTaxSitus,
    getFieldState,
    getValues,
    setValue,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      submit: () => formMethods.handleSubmit(onSubmit)(),
      reset: () =>
        formMethods.reset({
          holdingsGroupId: holdingsGroup?.id || null,
          holdingType: 'asset',
          structuredAssetsSubform: defaultStructuredAssetsData,
          valuationAsOfDate: new Date(),
        }),
    }),
    [formMethods, holdingsGroup?.id, defaultStructuredAssetsData, onSubmit]
  );

  return (
    <FormProvider {...formMethods}>
      <AssetFormContent
        holdingsGroup={holdingsGroup}
        control={formMethods.control}
        disabled={disabled}
        emptyAsset={emptyAsset}
      />
    </FormProvider>
  );
});

AssetHoldingForm.displayName = 'AssetHoldingForm';

interface AssetFormContentProps {
  holdingsGroup?: EstatePage_EntityFragment;
  control: Control<AssetHoldingsFormShape>;
  disabled: boolean;
  emptyAsset: StructuredAsset;
}

function AssetFormContent({
  control,
  disabled,
  emptyAsset,
}: AssetFormContentProps) {
  // Watch the assets array to calculate the sum dynamically
  const assets = useWatch({
    name: 'structuredAssetsSubform.assets',
    control,
  }) as Array<{ value: Decimal }> | undefined;

  const assetValuesSum = useMemo(() => {
    if (!assets || assets.length === 0) {
      return new Decimal(0);
    }

    return assets.reduce((sum, asset) => {
      const value = asset?.value;
      if (value != null && Decimal.isDecimal(value)) {
        return sum.add(value);
      }
      return sum;
    }, new Decimal(0));
  }, [assets]);

  const { options: assetCategoryOptions, loading: formOptionsLoading } =
    useAssetCategoryOptions();

  return (
    <FormFieldsDisabledProvider
      isDisabled={Boolean(formOptionsLoading || disabled)}
    >
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          {/* Guard the initial render until we have the default category option */}
          {emptyAsset.categoryId && (
            <FormAwareInputRepeater<AssetHoldingsFormShape>
              name={'structuredAssetsSubform.assets'}
              emptyValue={emptyAsset}
              control={control}
              shouldInit={false}
              allowEmpty={false}
              addAnotherItemText={`Add additional asset or account`}
              addAnotherItemButtonProps={{
                size: 'sm',
                variant: 'transparent',
                sx: {
                  ...getCSSPropsForButtonPadding('sm', false),
                },
              }}
              rightFooterContent={
                // Footer slot is 1/3 of the form; constrain the sumline
                // to 3/4 of that slot to align under the Value column.
                <Box display="flex" justifyContent="flex-end">
                  <Box sx={{ width: '75%' }}>
                    <InputRepeaterSumRow
                      content={formatCurrencyNoDecimals(assetValuesSum)}
                    />
                  </Box>
                </Box>
              }
              render={(i: number) => {
                const isFirstInput = i === 0;
                const columnWidth = 3;
                return (
                  <FormLayoutRow>
                    <FormLayoutItem width={columnWidth}>
                      <AssetTitleField
                        index={i}
                        hideLabel={!isFirstInput}
                        label="Name"
                      />
                    </FormLayoutItem>
                    <FormLayoutItem width={columnWidth}>
                      <AssetCategoryField
                        index={i}
                        hideLabel={!isFirstInput}
                        options={assetCategoryOptions}
                      />
                    </FormLayoutItem>
                    <FormLayoutItem width={columnWidth}>
                      <AssetStateField index={i} hideLabel={!isFirstInput} />
                    </FormLayoutItem>
                    <FormLayoutItem width={columnWidth}>
                      <AssetValueField index={i} hideLabel={!isFirstInput} />
                    </FormLayoutItem>
                  </FormLayoutRow>
                );
              }}
            />
          )}
        </FormLayoutItem>
      </FormLayoutRow>
    </FormFieldsDisabledProvider>
  );
}
